import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Calendar, Clock, MapPin, Heart, Sparkles, CheckCircle2, Navigation, Volume2, VolumeX, User, Camera } from 'lucide-react';
import type { InvitationData, TemplateMetadata } from '../types';
import { registry } from '../registry';
import { Living } from '../../experiences/scene/interactive/Living';
import './theme.css';


gsap.registerPlugin(ScrollTrigger);

export const META: TemplateMetadata = {
  id: 'exp-gsap-shiva-parvati',
  name: 'Shiva Parvati Divine Marriage',
  occasionType: 'WEDDING',
  description: 'A majestic GSAP ScrollTrigger scroll-driven traditional Hindu gods invitation celebrating the divine marriage of Lord Shiva & Goddess Parvati on Mount Kailash with 3D perspective animations and sacred floating accents.',
  isPremium: true,
  tags: [
    'shiva-parvati',
    'gsap',
    'scroll-driven',
    'traditional',
    'wedding',
    'hindu-gods',
    'divine',
    'kailash',
    'om-namah-shivaya',
    'damru',
    'trishul',
    'sacred-matrimony',
    'cinematic',
    '3d',
    'scroll-experience',
    'ai-native',
    'shiva',
    'parvati',
    'mandap',
    'tilak',
    'phere'
  ],
  colorScheme: { primary: '#F4D068', accent: '#C87A32', bg: '#070B12' }
};

const ASSET_BASE = '/assets/templates/exp-shiva-parvati';

export function GSAPShivaParvatiTemplate({ data }: { data: InvitationData }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroBgRef = useRef<HTMLDivElement>(null);
  const blessingRef = useRef<HTMLDivElement>(null);
  const blessingBgRef = useRef<HTMLDivElement>(null);
  const serenadeRef = useRef<HTMLDivElement>(null);
  const serenadeBgRef = useRef<HTMLDivElement>(null);
  const ritualsRef = useRef<HTMLDivElement>(null);
  const vowsRef = useRef<HTMLDivElement>(null);
  const vowsBgRef = useRef<HTMLDivElement>(null);

  const [isRsvpOpen, setIsRsvpOpen] = useState(false);
  const [rsvpAttending, setRsvpAttending] = useState<'yes' | 'no' | null>(null);
  const [guestCount, setGuestCount] = useState(1);
  const [guestName, setGuestName] = useState('');
  const [guestNotes, setGuestNotes] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isMuted, setIsMuted] = useState(true);

  // Interactive Engagement States
  const [blessingsCount, setBlessingsCount] = useState(108);
  const [activeVows, setActiveVows] = useState<Record<number, boolean>>({});
  const [hasBlessed, setHasBlessed] = useState(false);

  const handleSendBlessing = () => {
    setBlessingsCount((prev) => prev + 1);
    setHasBlessed(true);
  };

  const toggleVow = (idx: number) => {
    setActiveVows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };


  // Type-safe Data Extraction with Fail-Safe Fallbacks
  const rawData = data as unknown as Record<string, any>;
  const customData = (data.customData || {}) as Record<string, any>;
  const weddingData = (customData.wedding || {}) as Record<string, any>;
  const expMeta = (customData.meta || customData.experienceMeta || {}) as Record<string, any>;

  const groomName =
    weddingData.groom?.fullName ||
    weddingData.groom?.nickname ||
    expMeta.groomName ||
    expMeta.secondaryName ||
    rawData.groomName ||
    (data.eventName ? data.eventName.split(/ (?:&|and) /i)[0]?.trim() : '') ||
    'Shivansh';

  const brideName =
    weddingData.bride?.fullName ||
    weddingData.bride?.nickname ||
    expMeta.brideName ||
    expMeta.primaryName ||
    rawData.brideName ||
    (data.eventName ? data.eventName.split(/ (?:&|and) /i)[1]?.trim() : '') ||
    'Parvati';

  const eventTitle = rawData.title || expMeta.tagline || data.eventName || 'Sacred Matrimony of Shiv & Parvati';
  const eventDate = rawData._dateInput || data.eventDate || expMeta.weddingDate || expMeta.eventDate || 'November 24, 2026';
  const eventTime = rawData.eventTime || expMeta.eventTime || '10:00 AM IST';
  const venueName = data.eventVenue || expMeta.venueName || weddingData.venueName || 'Kailash Mandap, Himalayan Heights';
  const venueAddress =
    weddingData.venueAddress || expMeta.venueAddress || data.eventVenue || 'Sacred Mount Kailash Valley, Uttarakhand';
  const customMessage =
    rawData.customMessage ||
    expMeta.tagline ||
    expMeta.customMessage ||
    data.customMessage ||
    'With the divine blessings of Lord Shiva & Goddess Parvati, we solicit your presence on this auspicious day.';
  const hostName = data.hostName || expMeta.hostedBy || expMeta.hostName || 'Chopra & Kapoor Families';
  const customVideoUrl = customData.customVideoUrl || expMeta.customVideoUrl || customData.preWeddingVideoUrl;

  // Map link constructor
  const googleMapsUrl =
    data.rsvpLink ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venueName} ${venueAddress}`)}`;

  // Uploaded couple photos & gallery — real guest-facing content, not mock/AI artwork
  const groomPhoto: string | undefined =
    expMeta.referenceImages?.groom ||
    expMeta.groomPhotoUrl ||
    expMeta.groomPhoto ||
    customData.groomPhotoUrl ||
    customData.groomPhoto ||
    weddingData.groom?.photoUrl;

  const bridePhoto: string | undefined =
    expMeta.referenceImages?.bride ||
    expMeta.bridePhotoUrl ||
    expMeta.bridePhoto ||
    customData.bridePhotoUrl ||
    customData.bridePhoto ||
    weddingData.bride?.photoUrl;

  const couplePhoto: string | undefined =
    expMeta.referenceImages?.couple ||
    expMeta.couplePhotoUrl ||
    expMeta.couplePhoto ||
    customData.couplePhotoUrl ||
    customData.couplePhoto ||
    weddingData.couplePhotoUrl;

  const galleryPhotos: string[] = data.mediaUrls ?? [];
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);

  // Dynamic Ceremonies Extraction
  const customEvents =
    customData.ceremonies ||
    customData.events ||
    expMeta.rituals ||
    expMeta.events ||
    expMeta.ceremonies;

  const defaultEventsList = [
    {
      title: 'Shree Ganesh Pujan & Tilak',
      time: '09:00 AM',
      date: eventDate,
      venue: venueName,
      desc: 'Auspicious Vedic rituals invoking Lord Ganesha & divine tilak ceremony.',
      bgImage: `${ASSET_BASE}/shiva_blessing_bg.jpg`
    },
    {
      title: 'Sangeet & Himalayan Cosmic Garba',
      time: '06:00 PM',
      date: eventDate,
      venue: 'Kailash Amphitheatre',
      desc: 'A night of celestial music, Damru rhythm beats, and traditional Garba.',
      bgImage: `${ASSET_BASE}/shiva_parvati_hero_bg.jpg`
    },
    {
      title: 'Sacred Vivah Phere & Mandap',
      time: '11:00 AM',
      date: eventDate,
      venue: venueName,
      desc: '7 Sacred vows around the holy agni fire in presence of family and divine deities.',
      bgImage: `${ASSET_BASE}/kailash_mandap_bg.jpg`
    }
  ];

  const bgImagesList = [
    `${ASSET_BASE}/shiva_blessing_bg.jpg`,
    `${ASSET_BASE}/shiva_parvati_hero_bg.jpg`,
    `${ASSET_BASE}/kailash_mandap_bg.jpg`
  ];

  const eventsList =
    Array.isArray(customEvents) && customEvents.length > 0
      ? customEvents.map((evt: any, idx: number) => ({
          title: evt.name || evt.title || `Ceremony ${idx + 1}`,
          time: evt.time || evt.eventTime || eventTime,
          date: evt.date || evt.eventDate || eventDate,
          venue: evt.venue || evt.venueName || venueName,
          desc: evt.description || evt.desc || 'Auspicious celebratory ceremony.',
          bgImage: bgImagesList[idx % bgImagesList.length]
        }))
      : defaultEventsList;


  // 7 Sacred Vows of Shiva & Parvati
  const sacredVows = [
    'First Vow: Nourishment & Cosmic Harmony (अन्नं पत्ये)',
    'Second Vow: Physical & Spiritual Strength (बलं पत्ये)',
    'Third Vow: Prosperity & Sacred Duties (धनं पत्ये)',
    'Fourth Vow: Joy & Eternal Family Well-being (सुखं पत्ये)',
    'Fifth Vow: Divine Offspring & Lineage Protection (प्रजापतिः)',
    'Sixth Vow: Health & Seasonal Balance (ऋतुभ्यः)',
    'Seventh Vow: Lifelong Friendship & Eternal Union (सखा सप्ता)'
  ];

  // Initialize GSAP ScrollTrigger timelines with clean unmount cleanup
  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Hero Text & 3D Depth Entrance
      gsap.fromTo(
        '.sp-hero-title',
        { opacity: 0, y: 40, scale: 0.9, rotateX: 15 },
        { opacity: 1, y: 0, scale: 1, rotateX: 0, duration: 1.4, ease: 'power3.out' }
      );

      gsap.fromTo(
        '.sp-hero-names',
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 1.6, delay: 0.3, ease: 'back.out(1.7)' }
      );

      // 1b. Hero Background — slow cosmic "breathing" zoom (no translate, zero overscan risk)
      if (heroBgRef.current) {
        gsap.to(heroBgRef.current, {
          scale: 1.18,
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      // 2. Scene 1: Shiva Parvati Blessing — background drifts sideways, card tilts in after
      if (blessingBgRef.current) {
        gsap.to(blessingBgRef.current, {
          x: -35,
          scale: 1.22,
          ease: 'none',
          scrollTrigger: {
            trigger: blessingRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        });
      }

      if (blessingRef.current) {
        gsap.fromTo(
          '.sp-blessing-card',
          { rotateY: -12, rotateX: 10, opacity: 0, scale: 0.92 },
          {
            rotateY: 0,
            rotateX: 0,
            opacity: 1,
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: blessingRef.current,
              start: 'top 55%',
              end: 'top 20%',
              scrub: 1
            }
          }
        );
      }

      // Kailash Serenade — background drifts the opposite way for a distinct feel
      if (serenadeBgRef.current) {
        gsap.to(serenadeBgRef.current, {
          x: 35,
          scale: 1.22,
          ease: 'none',
          scrollTrigger: {
            trigger: serenadeRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        });
      }

      if (serenadeRef.current) {
        gsap.fromTo(
          '.sp-serenade-copy',
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            scrollTrigger: {
              trigger: serenadeRef.current,
              start: 'top 55%',
              end: 'top 20%',
              toggleActions: 'play reverse play reverse'
            }
          }
        );
      }

      // 7 Sacred Vows — meditative scale-only zoom, no translate at all
      if (vowsBgRef.current) {
        gsap.to(vowsBgRef.current, {
          scale: 1.15,
          ease: 'none',
          scrollTrigger: {
            trigger: vowsRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          }
        });
      }

      // 3. Scene 2: Floating Parallax Divine Accents (Trishul, Moon, Bel Patra)
      gsap.utils.toArray<HTMLElement>('.sp-floating-accent').forEach((el, idx) => {
        const speed = (idx + 1) * 35;
        gsap.to(el, {
          y: -speed,
          rotate: idx % 2 === 0 ? 25 : -25,
          ease: 'none',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1.2
          }
        });
      });

      // 4. Scene 3: Ritual Schedule Stagger Reveal
      if (ritualsRef.current) {
        gsap.fromTo(
          '.sp-ritual-card',
          { opacity: 0, y: 50, rotateX: 10 },
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            stagger: 0.2,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ritualsRef.current,
              start: 'top 75%'
            }
          }
        );
      }

      // 5. Scene 4: 7 Sacred Vows 3D Depth Card reveal
      if (vowsRef.current) {
        gsap.fromTo(
          '.sp-vow-item',
          { opacity: 0, x: -30 },
          {
            opacity: 1,
            x: 0,
            stagger: 0.15,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: vowsRef.current,
              start: 'top 75%'
            }
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleRsvpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    setIsSubmitted(true);
  };

  return (
    <div ref={containerRef} className="sp-template-root min-h-screen relative overflow-hidden text-white">
      {/* Dynamic Audio Toggle */}
      <button
        onClick={() => setIsMuted(!isMuted)}
        aria-label="Toggle Audio"
        className="fixed top-5 right-5 z-50 bg-[#0F192E]/80 backdrop-blur-md p-3 rounded-full border border-[#F4D068]/40 text-[#F4D068] hover:bg-[#F4D068] hover:text-[#070B12] transition-all shadow-xl"
      >
        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5 animate-pulse" />}
      </button>

      {/* Floating Background Ambient Particles */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="sp-floating-accent absolute top-12 left-6 text-3xl opacity-60 sp-animate-float">🌿</div>
        <div className="sp-floating-accent absolute top-1/4 right-8 text-4xl opacity-70 sp-animate-float" style={{ animationDelay: '1.5s' }}>🔱</div>
        <div className="sp-floating-accent absolute top-1/2 left-10 text-3xl opacity-60 sp-animate-float" style={{ animationDelay: '2.5s' }}>🌙</div>
        <div className="sp-floating-accent absolute top-2/3 right-12 text-3xl opacity-70 sp-animate-float" style={{ animationDelay: '3.5s' }}>🥁</div>
        <div className="sp-floating-accent absolute bottom-24 left-8 text-4xl opacity-70 sp-animate-float" style={{ animationDelay: '4.5s' }}>🪷</div>
      </div>

      {/* ================= HERO SECTION (SINGLE SCROLL FULL-BLEED ABOVE-THE-FOLD) ================= */}
      <section ref={heroRef} className="sp-hero-section relative z-10 text-center w-full min-h-screen flex flex-col justify-center items-center px-4 py-16 border-b border-[#F4D068]/20">
        {/* Hero Backdrop Artwork — Full Bleed */}
        <div ref={heroBgRef} className="absolute inset-0 z-0 pointer-events-none overflow-hidden transform-gpu">
          <img
            src={`${ASSET_BASE}/shiva_parvati_hero_bg.jpg`}
            alt="Shiva Parvati Kailash Wedding"
            className="w-full h-full object-cover object-center filter brightness-100 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070B12]/80 via-[#0F192E]/50 to-[#070B12]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto pt-8 pb-4">
          {/* Sacred Auspicious Shloka */}
          <div className="inline-block px-5 py-1.5 rounded-full border border-[#F4D068]/40 bg-[#0F192E]/80 backdrop-blur-md mb-4 shadow-lg">
            <span className="sp-font-devanagari text-[#F4D068] font-bold text-sm tracking-wider">
              ॥ ॐ नमः शिवाय ॥
            </span>
          </div>

          <h2 className="sp-hero-title sp-font-devanagari text-lg sm:text-xl text-[#F4D068] font-semibold tracking-wide mb-2">
            शिव-पार्वती पावन विवाह उत्सव
          </h2>

          <h1 className="sp-hero-names sp-font-cinzel text-3xl sm:text-5xl md:text-6xl font-extrabold sp-gold-text my-4 leading-tight">
            {groomName} & {brideName}
          </h1>

          <p className="sp-font-script text-2xl sm:text-3xl text-[#FFF5C0] my-2">{eventTitle}</p>

          {hostName && (
            <p className="sp-font-devanagari text-xs sm:text-sm text-[#F4D068]/90 tracking-widest uppercase mt-1">
              Hosted with joy by {hostName}
            </p>
          )}
        </div>


        {/* Above-the-fold Ceremony Summary Cards */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-4xl mx-auto my-6 px-4">
          <div className="sp-glass-card p-4 flex items-center space-x-3 text-left border border-[#F4D068]/30">
            <Calendar className="w-6 h-6 text-[#F4D068] shrink-0" />
            <div>
              <p className="text-xs text-gray-300 font-medium uppercase">Date</p>
              <p className="text-sm font-semibold text-white">{eventDate}</p>
            </div>
          </div>

          <div className="sp-glass-card p-4 flex items-center space-x-3 text-left border border-[#F4D068]/30">
            <Clock className="w-6 h-6 text-[#F4D068] shrink-0" />
            <div>
              <p className="text-xs text-gray-300 font-medium uppercase">Time</p>
              <p className="text-sm font-semibold text-white">{eventTime}</p>
            </div>
          </div>

          <div className="sp-glass-card p-4 flex items-center space-x-3 text-left border border-[#F4D068]/30">
            <MapPin className="w-6 h-6 text-[#F4D068] shrink-0" />
            <div className="truncate">
              <p className="text-xs text-gray-300 font-medium uppercase">Venue</p>
              <p className="text-sm font-semibold text-white truncate">{venueName}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons Above-the-fold */}
        <div className="relative z-10 pb-8 flex flex-wrap justify-center gap-4">
          <button onClick={() => setIsRsvpOpen(true)} className="sp-btn-primary flex items-center space-x-2">
            <Heart className="w-4 h-4 fill-current" />
            <span>Confirm RSVP</span>
          </button>

          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-full border border-[#F4D068]/50 bg-[#0F192E]/80 backdrop-blur-md text-[#F4D068] font-semibold text-sm hover:bg-[#F4D068] hover:text-[#070B12] transition-all flex items-center space-x-2"
          >
            <Navigation className="w-4 h-4" />
            <span>Get Directions</span>
          </a>
        </div>
      </section>

      {/* ================= DIVINE COUPLE PORTRAITS (Real Uploaded Photos, When Provided) ================= */}
      {(groomPhoto || bridePhoto || couplePhoto) && (
        <section className="relative z-10 py-16 px-4 w-full flex items-center justify-center">
          <div className="sp-glass-card p-8 sm:p-10 border border-[#F4D068]/50 shadow-2xl bg-[#0F192E]/85 backdrop-blur-lg max-w-3xl mx-auto w-full flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <div className="flex flex-col items-center text-center">
              {bridePhoto ? (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#F4D068] shadow-lg overflow-hidden mb-3 bg-[#070B12]">
                  <img src={bridePhoto} alt={`${brideName}, the bride`} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#F4D068]/40 shadow-lg mb-3 bg-[#070B12] flex items-center justify-center">
                  <User className="w-10 h-10 text-[#F4D068]/40" />
                </div>
              )}
              <h3 className="sp-font-cinzel text-lg sm:text-xl font-bold sp-gold-text">{brideName}</h3>
              <span className="sp-font-devanagari text-xs text-[#F4D068]/80 uppercase tracking-widest mt-1">Bride</span>
            </div>

            <div className="text-[#F4D068] text-3xl font-bold font-script">&</div>

            <div className="flex flex-col items-center text-center">
              {groomPhoto ? (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#F4D068] shadow-lg overflow-hidden mb-3 bg-[#070B12]">
                  <img src={groomPhoto} alt={`${groomName}, the groom`} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-[#F4D068]/40 shadow-lg mb-3 bg-[#070B12] flex items-center justify-center">
                  <User className="w-10 h-10 text-[#F4D068]/40" />
                </div>
              )}
              <h3 className="sp-font-cinzel text-lg sm:text-xl font-bold sp-gold-text">{groomName}</h3>
              <span className="sp-font-devanagari text-xs text-[#F4D068]/80 uppercase tracking-widest mt-1">Groom</span>
            </div>
          </div>
        </section>
      )}

      {/* ================= PHOTO GALLERY LIGHTBOX (When Photos are Uploaded) ================= */}
      {galleryPhotos.length > 0 && (
        <section className="relative z-10 py-12 px-4 max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Camera className="w-5 h-5 text-[#F4D068]" />
            <h2 className="sp-font-cinzel text-2xl font-bold sp-gold-text">Photo Showcase Gallery</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {galleryPhotos.map((photo, idx) => (
              <div
                key={idx}
                onClick={() => setLightboxPhoto(photo)}
                className="relative aspect-square rounded-2xl overflow-hidden border border-[#F4D068]/30 cursor-pointer group shadow-lg"
              >
                <img src={photo} alt={`Gallery photo ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-semibold uppercase tracking-wider bg-[#0F192E]/80 px-3 py-1.5 rounded-full border border-[#F4D068]/40">View</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Photo Lightbox Modal */}
      {lightboxPhoto && (
        <div
          onClick={() => setLightboxPhoto(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md cursor-pointer"
        >
          <img
            src={lightboxPhoto}
            alt="Enlarged gallery photo"
            className="max-w-full max-h-[85vh] rounded-2xl border border-[#F4D068]/50 shadow-2xl object-contain"
          />
        </div>
      )}

      {/* ================= PRE-WEDDING STORY VIDEO PLAYER (When Video Link Provided) ================= */}
      {customVideoUrl && (
        <section className="relative z-10 py-12 px-4 max-w-4xl mx-auto text-center">
          <div className="sp-glass-card p-6 border border-[#F4D068]/40 shadow-2xl relative overflow-hidden bg-[#0F192E]/85">
            <h3 className="sp-font-cinzel text-xl font-bold sp-gold-text mb-4">Our Pre-Wedding Story & Special Memories</h3>
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video max-w-2xl mx-auto border border-[#F4D068]/30">
              <video
                src={customVideoUrl}
                controls
                className="w-full h-full object-cover"
                poster={`${ASSET_BASE}/shiva_parvati_hero_bg.jpg`}
              />
            </div>
          </div>
        </section>
      )}



      {/* ================= SCENE 1: SHREE SHIVA PARVATI BLESSING (FULL-BLEED NEELKANTH BLUE BACKGROUND) ================= */}
      <section ref={blessingRef} className="w-full min-h-[90vh] sm:min-h-screen relative flex items-center justify-center py-20 px-4 text-center overflow-hidden sp-3d-viewport">
        {/* Full-bleed Backdrop Image — 100% Width & Height with Neelkanth Sapphire Overlays */}
        <div ref={blessingBgRef} className="absolute inset-0 z-0 pointer-events-none overflow-hidden transform-gpu">
          <img
            src={`${ASSET_BASE}/shiva_blessing_bg.jpg`}
            alt="Shiva Parvati Divine Blessing"
            className="w-full h-full object-cover object-center filter brightness-105 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060D1A]/85 via-[#0A244D]/50 to-[#060D1A]/95" />
        </div>

        <div className="sp-blessing-card relative z-10 max-w-3xl mx-auto px-6 py-10 rounded-3xl sp-blue-card border border-[#38BDF8]/50 shadow-[0_0_40px_rgba(56,189,248,0.3)]">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#38BDF8]/15 border border-[#38BDF8]/50 flex items-center justify-center text-3xl shadow-[0_0_20px_rgba(56,189,248,0.4)]">
            🔱
          </div>
          <h3 className="sp-font-devanagari text-2xl sm:text-3xl font-bold text-[#F4D068] mb-4 drop-shadow-md">
            ॥ कर्पूरगौरं करुणावतारं संसारसारम् भुजगेन्द्रहारम् ॥
          </h3>
          <p className="text-gray-100 leading-relaxed text-base sm:text-lg max-w-2xl mx-auto italic font-medium mb-6">
            "{customMessage}"
          </p>

          {/* Interactive Divine Blessings Button */}
          <button
            onClick={handleSendBlessing}
            className={`px-6 py-3 rounded-full border transition-all duration-300 flex items-center justify-center space-x-2 mx-auto shadow-lg text-sm font-semibold ${
              hasBlessed
                ? 'bg-[#38BDF8]/20 border-[#38BDF8] text-[#38BDF8] scale-105 shadow-[0_0_25px_rgba(56,189,248,0.5)]'
                : 'bg-[#0C1A36]/80 border-[#F4D068]/50 text-[#F4D068] hover:bg-[#F4D068] hover:text-[#060D1A]'
            }`}
          >
            <Sparkles className="w-4 h-4 animate-spin-slow" />
            <span>{hasBlessed ? 'Divine Blessing Sent! 🙏' : 'Send Shower of Blessings 🌸'}</span>
            <span className="bg-[#38BDF8]/30 px-2 py-0.5 rounded-full text-xs text-white ml-2">{blessingsCount}</span>
          </button>
        </div>
      </section>

      {/* ================= SCENE 2: KAILASH SERENADE & FULL-BLEED SAPPHIRE BACKGROUND ================= */}
      <section ref={serenadeRef} className="w-full min-h-[90vh] sm:min-h-screen relative flex items-center justify-center py-20 px-4 text-center overflow-hidden">
        <div ref={serenadeBgRef} className="absolute inset-0 z-0 pointer-events-none transform-gpu">
          <img
            src={`${ASSET_BASE}/shiva_parvati_hero_bg.jpg`}
            alt="Kailash Cosmic Garden"
            className="w-full h-full object-cover object-center filter brightness-105 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060D1A]/85 via-[#0A244D]/45 to-[#060D1A]/95" />
        </div>

        <div className="sp-serenade-copy relative z-10 max-w-3xl mx-auto px-6 py-10 rounded-3xl sp-blue-card border border-[#38BDF8]/45 shadow-[0_0_35px_rgba(56,189,248,0.25)]">
          <span className="sp-font-script text-3xl sm:text-4xl text-[#F4D068] block mb-2">Himalayan Celestial Matrimony</span>
          <h2 className="sp-font-cinzel text-3xl sm:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            The Divine Union of Shiva & Shakti
          </h2>
          <p className="text-gray-200 text-base sm:text-lg leading-relaxed font-normal">
            Join us amidst snow-capped peaks, cosmic mantras, and sacred agni as Shivansh & Parvati begin their eternal journey of love, commitment, and divine togetherness.
          </p>
        </div>
      </section>

      {/* ================= SCENE 3: SACRED CEREMONIAL SCHEDULE ================= */}
      <section ref={ritualsRef} className="w-full min-h-screen relative py-20 px-4 flex flex-col justify-center items-center max-w-6xl mx-auto">
        <div className="text-center mb-10 relative z-10">
          <span className="sp-font-devanagari text-[#38BDF8] font-semibold text-sm uppercase tracking-widest block mb-1">
            पावन वैवाहिक कार्यक्रम
          </span>
          <h2 className="sp-font-cinzel text-3xl sm:text-5xl font-bold sp-gold-text">
            Sacred Ceremony Schedule
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full relative z-10">
          {eventsList.map((event, idx) => (
            <div
              key={idx}
              className="sp-ritual-card sp-blue-card p-6 border border-[#38BDF8]/40 flex flex-col justify-between hover:border-[#38BDF8] transition-all duration-300 transform relative overflow-hidden shadow-xl hover:shadow-[0_0_35px_rgba(56,189,248,0.45)] group min-h-[320px] rounded-2xl"
            >
              <div className="absolute inset-0 z-0 pointer-events-none">
                <img
                  src={event.bgImage}
                  alt={event.title}
                  className="w-full h-full object-cover object-center filter brightness-95 contrast-110 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060D1A]/95 via-[#0C1A36]/75 to-[#0C1A36]/35" />
              </div>

              <div className="relative z-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#38BDF8]/20 border border-[#38BDF8]/50 text-[#38BDF8] mb-3">
                  {event.time}
                </span>
                <h3 className="sp-font-cinzel text-xl font-bold text-white mb-2 group-hover:text-[#F4D068] transition-colors">
                  {event.title}
                </h3>
                <p className="text-xs text-gray-300 leading-relaxed mb-4">{event.desc}</p>
              </div>

              <div className="relative z-10 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-[#F4D068]">
                <span className="font-semibold">{event.date}</span>
                <span className="truncate max-w-[140px] opacity-80">{event.venue}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SCENE 4: 7 SACRED VOWS (FULL-BLEED SAPPHIRE BACKGROUND) ================= */}
      <section ref={vowsRef} className="w-full min-h-[90vh] sm:min-h-screen relative flex items-center justify-center py-20 px-4 text-center overflow-hidden">
        {/* Full-bleed Mandap Backdrop */}
        <div ref={vowsBgRef} className="absolute inset-0 z-0 pointer-events-none transform-gpu">
          <img
            src={`${ASSET_BASE}/kailash_mandap_bg.jpg`}
            alt="Divine Union Backdrop"
            className="w-full h-full object-cover object-center filter brightness-105 contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060D1A]/90 via-[#0A244D]/55 to-[#060D1A]/95" />
        </div>

        {/* Sacred agni ambiance — floating ember particles around the 7 vows */}
        <Living motionType="fire" className="absolute inset-0 z-[5] pointer-events-none">
          {null}
        </Living>

        <div className="relative z-10 max-w-3xl mx-auto px-6 py-10 rounded-3xl sp-blue-card border border-[#38BDF8]/45 shadow-[0_0_40px_rgba(56,189,248,0.3)]">
          <span className="sp-font-devanagari text-[#38BDF8] font-bold text-sm tracking-widest block mb-2">
            सप्तपदी - सात वचन
          </span>

          <h2 className="sp-font-cinzel text-3xl font-bold text-white mb-2">7 Sacred Vows of Divine Union</h2>
          <p className="text-xs text-[#38BDF8]/80 mb-6 uppercase tracking-wider">Click any vow to offer your sacred blessing</p>

          <div className="grid grid-cols-1 gap-3 max-w-2xl mx-auto text-left">
            {sacredVows.map((vow, idx) => {
              const isChecked = !!activeVows[idx];
              return (
                <div
                  key={idx}
                  onClick={() => toggleVow(idx)}
                  className={`sp-vow-item p-3.5 rounded-xl border flex items-center space-x-3 text-sm transition-all duration-300 cursor-pointer shadow-md ${
                    isChecked
                      ? 'bg-[#38BDF8]/20 border-[#38BDF8] text-white shadow-[0_0_20px_rgba(56,189,248,0.4)]'
                      : 'bg-[#0C1A36]/85 border-[#38BDF8]/30 text-gray-200 hover:border-[#38BDF8]'
                  }`}
                >
                  <Sparkles className={`w-4 h-4 shrink-0 transition-transform ${isChecked ? 'text-[#38BDF8] scale-125' : 'text-[#F4D068]'}`} />
                  <span className="flex-1">{vow}</span>
                  {isChecked && <span className="text-[#38BDF8] font-bold text-xs bg-[#38BDF8]/30 px-2 py-0.5 rounded-full">Blessed ✨</span>}
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ================= FOOTER & RSVP CTA ================= */}
      <footer className="relative z-10 py-12 px-4 text-center border-t border-[#F4D068]/20 bg-[#070B12]/90 backdrop-blur-md">
        <p className="sp-font-script text-3xl text-[#F4D068] mb-2">Har Har Mahadev</p>
        <p className="text-sm text-gray-400 mb-6">We await your presence & divine blessings</p>

        <button onClick={() => setIsRsvpOpen(true)} className="sp-btn-primary">
          RSVP Now
        </button>
      </footer>

      {/* ================= RSVP MODAL ================= */}
      {isRsvpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="sp-glass-card max-w-md w-full p-6 border border-[#F4D068] relative bg-[#0F192E] text-white shadow-2xl">
            <button
              onClick={() => setIsRsvpOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-lg font-bold"
            >
              ✕
            </button>

            {!isSubmitted ? (
              <form onSubmit={handleRsvpSubmit} className="space-y-4 text-left">
                <h3 className="sp-font-cinzel text-xl font-bold text-[#F4D068] text-center">RSVP Confirmation</h3>
                <p className="text-xs text-gray-300 text-center">Kindly confirm your presence by filling in your details below.</p>

                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Your Full Name *</label>
                  <input
                    type="text"
                    required
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Enter guest name"
                    className="w-full px-3 py-2 rounded-lg bg-[#070B12] border border-[#F4D068]/30 text-white focus:outline-none focus:border-[#F4D068] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Will you attend? *</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRsvpAttending('yes')}
                      className={`py-2 text-xs font-bold rounded-lg border ${
                        rsvpAttending === 'yes'
                          ? 'bg-[#F4D068] text-[#070B12] border-[#F4D068]'
                          : 'bg-[#070B12] text-gray-300 border-gray-700'
                      }`}
                    >
                      Yes, Attending
                    </button>
                    <button
                      type="button"
                      onClick={() => setRsvpAttending('no')}
                      className={`py-2 text-xs font-bold rounded-lg border ${
                        rsvpAttending === 'no'
                          ? 'bg-[#F4D068] text-[#070B12] border-[#F4D068]'
                          : 'bg-[#070B12] text-gray-300 border-gray-700'
                      }`}
                    >
                      Regretfully Decline
                    </button>
                  </div>
                </div>

                {rsvpAttending === 'yes' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1">Number of Guests</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={guestCount}
                      onChange={(e) => setGuestCount(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 rounded-lg bg-[#070B12] border border-[#F4D068]/30 text-white focus:outline-none focus:border-[#F4D068] text-sm"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Blessings / Warm Message</label>
                  <textarea
                    rows={3}
                    value={guestNotes}
                    onChange={(e) => setGuestNotes(e.target.value)}
                    placeholder="Write your wishes for Shiv & Parvati..."
                    className="w-full px-3 py-2 rounded-lg bg-[#070B12] border border-[#F4D068]/30 text-white focus:outline-none focus:border-[#F4D068] text-sm"
                  />
                </div>

                <button type="submit" className="sp-btn-primary w-full mt-2">
                  Submit RSVP
                </button>
              </form>
            ) : (
              <div className="text-center py-8 space-y-4">
                <CheckCircle2 className="w-16 h-16 text-[#F4D068] mx-auto animate-bounce" />
                <h3 className="sp-font-cinzel text-2xl font-bold text-white">RSVP Received!</h3>
                <p className="text-sm text-gray-300">
                  Thank you <span className="text-[#F4D068] font-bold">{guestName}</span>. Your response has been recorded. May Lord Shiva & Goddess Parvati bless you!
                </p>
                <button onClick={() => setIsRsvpOpen(false)} className="sp-btn-primary">
                  Close Window
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: GSAPShivaParvatiTemplate });

